import { Injectable } from '@angular/core';

import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { RegionVisibilityService } from '@frontend/sports/betting-offer/feature/region-visibility';
import { SitecoreEventDetailsConfig } from '@frontend/sports/common/client-config-data-access';
import { CompetitionLogosService } from '@frontend/sports/competition-logos/feature';
import { AdaptiveLayoutService } from '@frontend/sports/layout/feature';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { Observable, debounceTime, map } from 'rxjs';

import { BreadcrumbModel } from '../breadcrumbs/breadcrumb.models';
import { BreadcrumbSlotResolve, BreadcrumbsResolve } from '../breadcrumbs/breadcrumbs.resolver';
import { DetailedEventViewModel } from '../event-details-common/event-details-common.models';
import { EventDetailsBreadcrumbComponent } from './event-details-breadcrumb.component';

@Injectable({ providedIn: 'root' })
export class EventDetailsBreadcrumbsResolver extends BreadcrumbsResolve<DetailedEventViewModel> {
    constructor(
        private urlHelper: UrlHelperService,
        private sitecoreEventDetails: SitecoreEventDetailsConfig,
        private competitionLogosService: CompetitionLogosService,
        private adaptiveLayout: AdaptiveLayoutService,
        private regionVisibilityService: RegionVisibilityService,
    ) {
        super();
    }

    protected customResolve(viewModel: DetailedEventViewModel): Observable<BreadcrumbModel[] | BreadcrumbSlotResolve> {
        return this.sitecoreEventDetails.whenReady.pipe(
            debounceTime(10),
            map(() => {
                if (!viewModel || !viewModel.model || !viewModel.model.published) {
                    return [];
                }

                const event = viewModel.model;
                const crumbs = this.getCrumbs(event);

                if (this.adaptiveLayout.current.headerSubNavigation || this.adaptiveLayout.current.columns < 3) {
                    return {
                        component: EventDetailsBreadcrumbComponent,
                        schemaBreadcrumbs: crumbs,
                    };
                } else {
                    return crumbs;
                }
            }),
        );
    }

    protected getCrumbs(event: EventModel): BreadcrumbModel[] {
        const name = event.name;
        let title = this.regionVisibilityService.shouldHide(event.region, event.league)
            ? `${event.league.name}`
            : `${event.region.name} | ${event.league.name}`;

        if (name.toLowerCase().includes(this.sitecoreEventDetails.eventDetails.NeutralVenue.toLowerCase())) {
            title += ` - ${this.sitecoreEventDetails.eventDetails.NeutralVenue}`;
        }
        const isWithRightImage = !!this.competitionLogosService.getLogoUrl(event.sport.id, event.league.id) || !!event.league.imageProfile;

        return [
            this.createCrumb({
                icon: '',
                title,
                url: this.urlHelper.getEventUrl(event),
                sportId: event.sport.id,
                leagueId: event.league.id,
                isWithRightImage,
                imageProfile: event.league.imageProfile,
            }),
        ];
    }
}
